import type { ReactNode } from 'react';
import { CombinedEuDossierView } from './components/CombinedEuDossierView';

/**
 * Which countries render an in-page "complete dossier" view on the result page
 * (vs. the generic single-run body). Resolver, not an `if country === 'EU'` in
 * the route — so a new country plugs its combined view in here and the result
 * route stays country-agnostic.
 *
 * Only EU has an assembled multi-part combined dossier (its annexes, fetched by
 * productId + deviceClass). US and Canada are single-document dossiers rendered
 * inline from the run's own response (see CompletedSection), so they correctly
 * return null here. A future multi-part country adds its `case` below.
 */
export function combinedDossierViewFor(
  country: string | null | undefined,
  props: { productId: number; deviceClass: string; runId?: number | null },
): ReactNode | null {
  switch ((country ?? '').toUpperCase()) {
    case 'EU':
      // EU medicinal (drug/biologic) is ONE CTD document keyed by an `art-*`
      // legal basis — a single-document dossier like US/CA, rendered inline by
      // the eCTD renderer (DossierResultRenderer detects module_1_administrative).
      // It is NOT the device multi-annex assembly, so it must not route to the
      // device combined view (whose endpoint 400s on a legal basis). Device
      // classes (MDR IIa/IIb/III, IVDR A-D) never start with `art-`, so the
      // device/IVDR path below is completely unchanged.
      if (props.deviceClass.startsWith('art-')) return null;
      return (
        <CombinedEuDossierView
          productId={props.productId}
          deviceClass={props.deviceClass}
          runId={props.runId ?? null}
        />
      );
    default:
      return null;
  }
}
